import httpClient from './httpClient';
import { buildQueryString } from '../utils/queryBuilder';
import { parseApiError } from '../utils/errorHandler';
import { buildAddressDtoFromForm } from '../utils/legalPersonMappers';

const ENDPOINT = '/api/enderecos/templates';

/**
 * Normaliza resposta paginada (HAL ou content direto) para o formato padrão do projeto.
 * @private
 */
function normalizePageResponse(json, fallbackPage) {
    const embedded = json._embedded;
    const content = embedded
        ? (embedded.addresses ?? embedded.address ?? embedded.content ?? Object.values(embedded)[0] ?? [])
        : (json.content ?? []);
    const list = Array.isArray(content) ? content : [];
    const pageMeta = json.page || {};
    const totalPages = pageMeta.totalPages ?? 0;
    const pageNumber = Math.max(0, pageMeta.number ?? fallbackPage ?? 0);
    return {
        content: list,
        page: {
            number: pageNumber,
            totalPages,
            first: totalPages === 0 || pageNumber === 0,
            last: totalPages === 0 || pageNumber >= totalPages - 1,
        },
    };
}

/**
 * GET /api/enderecos/templates
 * @param {Object} params
 * @param {number} [params.page=0]
 * @param {number} [params.size=10]
 * @param {string|number|null} [params.customerId] - obrigatório para empresa de transporte
 * @param {string} [params.name] - substring do apelido (case-insensitive no backend)
 */
async function list({ page = 0, size = 10, customerId, name } = {}) {
    const filters = {};
    if (customerId != null && String(customerId).trim() !== '') {
        filters.customerId = String(customerId);
    }
    const nameQuery = name != null ? String(name).trim() : '';
    if (nameQuery) {
        filters.name = nameQuery;
    }
    const queryString = buildQueryString({ page, size, filters });
    const response = await httpClient.get(`${ENDPOINT}?${queryString}`);
    if (!response.ok) {
        throw await parseApiError(response);
    }
    const json = await response.json();
    return normalizePageResponse(json, page);
}

/**
 * POST /api/enderecos/templates
 * @param {Object} addressForm - campos do AddressForm / useAddress
 * @param {string|number|null} [customerId] - empresa de transporte: cliente selecionado no corpo
 */
async function create(addressForm, customerId) {
    const dto = buildAddressDtoFromForm(addressForm);
    delete dto.id;

    const payload = { ...dto };
    if (customerId != null && String(customerId).trim() !== '') {
        payload.customerId = Number(customerId);
    }

    const response = await httpClient.post(ENDPOINT, payload);
    if (!response.ok) {
        throw await parseApiError(response);
    }
    const json = await response.json();
    return json.address ?? json;
}

/**
 * DELETE /api/enderecos/templates/{id}
 * @param {number|string} id
 * @param {string|number|null} [customerId] - empresa de transporte: query obrigatória
 */
async function remove(id, customerId) {
    const params = new URLSearchParams();
    if (customerId != null && String(customerId).trim() !== '') {
        params.append('customerId', String(customerId));
    }
    const queryString = params.toString();
    const url = queryString ? `${ENDPOINT}/${id}?${queryString}` : `${ENDPOINT}/${id}`;
    const response = await httpClient.delete(url);
    if (!response.ok) {
        throw await parseApiError(response);
    }
}

const addressTemplateService = {
    list,
    create,
    remove,
};

export default addressTemplateService;
